/**
 * PortalRegistryStrip — "Choose Your Portal" panel powered by the live
 * `/api/portals` registry. Renders every published portal as a rich card
 * with brand colour, tagline, owned scopes, and a "Visit portal →" CTA
 * pointing at the portal's canonical domain.
 *
 * This component is a pure gateway UI — it does NOT recreate portal
 * content. It orients visitors so the network router (SocialPulse.com)
 * can hand off cleanly to the specialist site that owns the scope.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

function statusLabel(s) {
  return s === "planned" ? "Planned" : s === "soft-launch" ? "Preview" : "Live";
}

function statusChip(s) {
  if (s === "planned")
    return "bg-slate-100 text-slate-500 ring-slate-200";
  if (s === "soft-launch")
    return "bg-amber-50 text-amber-700 ring-amber-200";
  return "bg-emerald-50 text-emerald-700 ring-emerald-200";
}

function scopeLabel(scope) {
  const { kind, key } = scope || {};
  if (!kind) return "";
  if (kind === "world") return "🌐 World";
  if (kind === "country") return key === "*" ? "Any country" : `Country · ${key}`;
  if (kind === "state") return key === "us:*" ? "US states" : key === "in:*" ? "India states" : `State · ${key.split(":").pop()}`;
  if (kind === "topic") return `Topic · ${key}`;
  return `${kind} · ${key}`;
}

export default function PortalRegistryStrip({ limit = null, showHeader = true, testid = "portal-registry-strip" }) {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/portals`, { timeout: 6000 });
        if (!cancelled) setPortals(r.data?.items || []);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load portals");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = limit ? portals.slice(0, limit) : portals;

  return (
    <section data-testid={testid} className="relative">
      {showHeader && (
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
              <Sparkles className="w-3.5 h-3.5" /> Choose your portal
            </div>
            <h2 className="mt-2 font-serif font-bold text-slate-900 text-xl sm:text-2xl">
              Every part of the network is a doorway.
            </h2>
            <p className="mt-1 text-sm text-slate-500 max-w-xl">
              SocialPulse.com is the router — pick the portal that fits what you're looking for.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {loading ? "Loading…" : `${portals.filter(p => p.status === "live").length} live portals`}
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Fetching the network directory…
        </div>
      ) : err ? (
        <div className="text-sm text-rose-600">Couldn't load portal registry ({err}).</div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testid}-list`}>
          {items.map((p) => (
            <li key={p.slug}>
              <a
                href={p.domain}
                target="_blank"
                rel="noopener"
                data-testid={`${testid}-item-${p.slug}`}
                className="group block rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden hover:ring-slate-300 hover:shadow-md transition-all h-full"
              >
                <div className="h-1.5" style={{ background: p.brand_color || "#7C3AED" }} />
                <div className="p-5 flex flex-col gap-3 h-full">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span
                        className="w-11 h-11 grid place-items-center rounded-2xl text-xl ring-1 ring-white shadow-sm"
                        style={{ background: `${p.brand_color}18`, color: p.brand_color }}
                      >
                        {p.logo_emoji || "◐"}
                      </span>
                      <div>
                        <h3 className="font-serif font-bold text-lg leading-tight text-slate-900 group-hover:text-slate-700">
                          {p.name}
                        </h3>
                        <p className="text-[11px] font-semibold uppercase tracking-[.15em] text-slate-500">
                          {p.domain.replace(/^https?:\/\//, "")}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full ring-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusChip(p.status)}`}
                    >
                      {statusLabel(p.status)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                    {p.tagline}
                  </p>
                  {(p.owned_scopes || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
                      {p.owned_scopes.slice(0, 3).map((s, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center rounded-full bg-slate-50 ring-1 ring-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600"
                        >
                          {scopeLabel(s)}
                        </span>
                      ))}
                    </div>
                  )}
                  <span
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold group-hover:gap-2 transition-all"
                    style={{ color: p.brand_color }}
                  >
                    Visit portal <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
